import React, { useContext } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { Bell, Check, CheckCheck, Heart, Trophy, Wallet, ShoppingBag, AlertCircle, Gift, X, ShieldAlert } from "lucide-react";
import { notificationsService } from "../services/notificationsService";
import OrgThemeContext from "../context/OrgThemeContext";

const typeIcons = {
  recognition_created: <Heart size={18} className="text-pink-500" />,
  recognition_approved: <Check size={18} className="text-green-500" />,
  recognition_rejected: <X size={18} className="text-red-500" />,
  nomination_submitted: <Trophy size={18} className="text-amber-500" />,
  nomination_approved: <Trophy size={18} className="text-green-500" />,
  campaign_started: <Gift size={18} className="text-blue-500" />,
  campaign_ended: <Gift size={18} className="text-gray-500" />,
  budget_exhausted: <AlertCircle size={18} className="text-red-500" />,
  wallet_credited: <Wallet size={18} className="text-green-500" />,
  reward_redeemed: <ShoppingBag size={18} className="text-purple-500" />,
  approval_pending: <ShieldAlert size={18} className="text-orange-500" />,
  system_alert: <AlertCircle size={18} className="text-blue-500" />,
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

const NotificationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const orgTheme = useContext(OrgThemeContext);

  const { data: notifications, isLoading } = useQuery({
    queryKey: ["notifications-all"],
    queryFn: () => notificationsService.getNotifications({ limit: 100 }),
    refetchInterval: 30000,
  });

  const markReadMutation = useMutation({
    mutationFn: notificationsService.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: notificationsService.markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications-all"] });
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count"] });
    },
  });

  const handleClick = (notification) => {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      // navigate() is a no-op when already on the target route; nudge the feed
      // (which refetches on this event) so new items appear.
      if (notification.action_url === location.pathname) {
        window.dispatchEvent(new CustomEvent("recognitionCreated"));
      }
    }
  };

  const unreadCount = notifications?.filter((n) => !n.is_read).length || 0;

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-500 mt-1">{unreadCount} unread</p>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={() => markAllReadMutation.mutate()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full transition-colors"
            style={{ color: orgTheme.primaryColor, backgroundColor: `${orgTheme.primaryColor}10` }}
          >
            <CheckCheck size={16} />
            Mark all read
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : !notifications || notifications.length === 0 ? (
        <div className="text-center py-16">
          <Bell size={48} className="mx-auto mb-3 text-gray-300" />
          <p className="text-gray-400">No notifications yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notifications.map((n) => (
            <button
              key={n.id}
              onClick={() => handleClick(n)}
              className={`w-full text-left px-4 py-4 flex items-start gap-3 rounded-xl border transition-colors ${
                !n.is_read
                  ? "bg-blue-50/60 border-blue-100 hover:bg-blue-50"
                  : "bg-white border-gray-100 hover:bg-gray-50"
              }`}
            >
              <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                {typeIcons[n.type] || <Bell size={18} className="text-gray-400" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className={`text-sm ${!n.is_read ? "font-semibold text-gray-900" : "text-gray-700"}`}>
                    {n.title}
                  </p>
                  {!n.is_read && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-0.5">{n.message}</p>
                <p className="text-xs text-gray-400 mt-1">{timeAgo(n.created_at)}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
